import React, { useEffect, useState } from 'react';
import { statsApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from '../components/DateRangePicker';
import { DataTable } from '../components/common/DataTable';
import { Badge } from '../components/ui/badge';
import {
  UsersIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

interface Client {
  chatId: string;
  chatType: string;
  chatTitle?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  totalTickets: number;
  newTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  urgentTickets: number;
  highPriorityTickets: number;
  lastActivity: string;
  firstActivity: string;
}

interface ClientsSummary {
  totalClients: number;
  totalTickets: number;
  activeClients: number;
}

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [summary, setSummary] = useState<ClientsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, [startDate, endDate]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await statsApi.getClients({ startDate, endDate });
      const data = response.data?.data;
      
      if (data) {
        setClients(data.clients || []);
        setSummary(data.summary || null);
      }
    } catch (error) {
      toast.error('Ошибка загрузки данных о клиентах');
    } finally {
      setLoading(false);
    }
  };

  const formatDateCompact = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins}м назад`;
    if (diffHours < 24) return `${diffHours}ч назад`;
    if (diffDays < 7) return `${diffDays}д назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  const getChatTypeLabel = (chatType: string) => {
    switch (chatType) {
      case 'private':
        return 'Личный';
      case 'group':
        return 'Группа';
      case 'supergroup':
        return 'Супергруппа';
      case 'channel':
        return 'Канал';
      default:
        return chatType;
    }
  };

  const getChatTypeIcon = (chatType: string) => {
    switch (chatType) {
      case 'private':
        return <UsersIcon className="h-4 w-4" />;
      case 'group':
      case 'supergroup':
      case 'channel':
        return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
      default:
        return <UsersIcon className="h-4 w-4" />;
    }
  };

  const handleClientClick = (client: Client) => {
    navigate(`/clients/${client.chatId}`, { 
      state: { 
        client,
        startDate,
        endDate
      } 
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center">
            <UsersIcon className="h-6 w-6 mr-2" />
            Клиенты
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Статистика по клиентам и чатам
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-2">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </div>
      </div>

      {/* Сводка */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="text-2xl font-bold text-foreground">
              {summary.totalClients}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Всего клиентов
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="text-2xl font-bold text-foreground">
              {summary.totalTickets}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Всего заявок
            </div>
          </div>
          <div className="bg-card border border-border p-4 rounded-lg">
            <div className="text-2xl font-bold text-foreground">
              {summary.activeClients}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Активных клиентов
            </div>
          </div>
        </div>
      )}

      {/* Таблица клиентов */}
      <div className="bg-card border border-border overflow-hidden">
        <DataTable<Client>
          data={clients}
          loading={loading}
          onRowClick={(client) => handleClientClick(client)}
          emptyMessage="Попробуйте изменить период или создайте первую заявку."
          getRowKey={(client) => client.chatId}
          columns={[
            {
              key: 'client',
              header: 'Клиент',
              className: 'px-3 py-2',
              cell: (client) => (
                <div className="flex items-center space-x-2">
                  <div className="text-muted-foreground">
                    {getChatTypeIcon(client.chatType)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {client.displayName}
                    </div>
                    <Badge variant="outline" className="text-xs mt-0.5">
                      {getChatTypeLabel(client.chatType)}
                    </Badge>
                  </div>
                </div>
              )
            },
            {
              key: 'total',
              header: 'Всего',
              className: 'px-3 py-2 text-center',
              cell: (client) => (
                <div className="flex items-center justify-center space-x-1">
                  <TicketIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {client.totalTickets}
                  </span>
                </div>
              )
            },
            {
              key: 'new',
              header: 'Новые',
              className: 'px-3 py-2 text-center',
              cell: (client) => (
                client.newTickets > 0 ? (
                  <Badge variant="outline" className="bg-status-new/10 text-status-new border-status-new/20">
                    {client.newTickets}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'inProgress',
              header: 'В работе',
              className: 'px-3 py-2 text-center',
              cell: (client) => (
                client.inProgressTickets > 0 ? (
                  <span className="text-sm font-medium text-status-assigned">
                    {client.inProgressTickets}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'resolved',
              header: 'Решено',
              className: 'px-3 py-2 text-center',
              cell: (client) => (
                client.resolvedTickets > 0 ? (
                  <span className="text-sm font-medium text-status-resolved">
                    {client.resolvedTickets}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'urgent',
              header: 'Срочные',
              className: 'px-3 py-2 text-center hidden lg:table-cell',
              cell: (client) => (
                client.urgentTickets > 0 ? (
                  <Badge variant="destructive" className="text-xs">
                    {client.urgentTickets}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'highPriority',
              header: 'Высокий',
              className: 'px-3 py-2 text-center hidden md:table-cell',
              cell: (client) => (
                client.highPriorityTickets > 0 ? (
                  <span className="text-sm font-medium text-priority-high">
                    {client.highPriorityTickets}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )
              )
            },
            {
              key: 'lastActivity',
              header: 'Активность',
              className: 'px-3 py-2 whitespace-nowrap hidden sm:table-cell',
              cell: (client) => (
                <span className="text-xs text-muted-foreground">
                  {formatDateCompact(client.lastActivity)}
                </span>
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default ClientsPage;
